import { ref, nextTick, getCurrentInstance } from 'vue'
import { ITierRow } from '@/utils/interfaces/iSalesAgentCommission'

export function useMandoranCommissionTiers() {
  const app = getCurrentInstance()
  const { $toast } = (app?.appContext.config.globalProperties || {}) as any

  const tiers = ref<ITierRow[]>([])
  const tiersGeneralError = ref<string>('')
  const originalTiers = ref<{ id: string; minPassenger?: number; maxPassenger?: number; commissionAmount?: number }[]>([])

  const validateTiers = (commissionType?: string | null): boolean => {
    if (commissionType && commissionType !== 'Tiered') {
      tiersGeneralError.value = ''
      return true
    }

    tiersGeneralError.value = ''
    if (tiers.value.length === 0) {
      tiersGeneralError.value = 'Minimal harus menambahkan 1 baris komisi'
      return false
    }

    let isValid = true

    for (let i = 0; i < tiers.value.length; i++) {
      const row = tiers.value[i]
      row.errorMin = ''
      row.errorMax = ''
      row.errorCommission = ''

      // 1. Min Passenger validation
      if (row.minPassenger === null || row.minPassenger === undefined || (row.minPassenger as any) === '') {
        row.errorMin = 'Min. Jumlah Penumpang harus diisi'
        isValid = false
      } else if (row.minPassenger < 0) {
        row.errorMin = 'Min. Jumlah Penumpang minimal 0'
        isValid = false
      } else if (i > 0) {
        const prevRow = tiers.value[i - 1]
        if (prevRow.maxPassenger !== null && prevRow.maxPassenger !== undefined && (prevRow.maxPassenger as any) !== '') {
          if (Number(row.minPassenger) <= Number(prevRow.maxPassenger)) {
            row.errorMin = `Min. Penumpang harus lebih besar dari Max. baris sebelumnya (${prevRow.maxPassenger})`
            isValid = false
          }
        }
      }

      // 2. Max Passenger validation
      if (row.maxPassenger === null || row.maxPassenger === undefined || (row.maxPassenger as any) === '') {
        row.errorMax = 'Max. Jumlah Penumpang harus diisi'
        isValid = false
      } else if (row.minPassenger !== null && row.minPassenger !== undefined && (row.minPassenger as any) !== '') {
        if (Number(row.maxPassenger) <= Number(row.minPassenger)) {
          row.errorMax = 'Max. Penumpang harus lebih besar dari Min. Penumpang'
          isValid = false
        }
      }

      // 3. Commission Amount validation
      if (row.commissionAmount === null || row.commissionAmount === undefined || (row.commissionAmount as any) === '') {
        row.errorCommission = 'Komisi harus diisi'
        isValid = false
      } else if (Number(row.commissionAmount) < 0 || Number(row.commissionAmount) > 99999) {
        row.errorCommission = 'Komisi minimal Rp 0 dan maksimal Rp 99.999'
        isValid = false
      }
    }

    return isValid
  }

  const addTierRow = () => {
    let nextMin: number | null = null
    if (tiers.value.length > 0) {
      const lastRow = tiers.value[tiers.value.length - 1]
      if (lastRow.maxPassenger !== null && lastRow.maxPassenger !== undefined && (lastRow.maxPassenger as any) !== '') {
        nextMin = Number(lastRow.maxPassenger) + 1
      }
    } else {
      nextMin = 0
    }

    tiers.value.push({
      minPassenger: nextMin,
      maxPassenger: null,
      commissionAmount: null,
      errorMin: '',
      errorMax: '',
      errorCommission: ''
    })
    tiersGeneralError.value = ''
  }

  const removeTierRow = (idx: number) => {
    if (idx !== tiers.value.length - 1) {
      if ($toast) {
        $toast.add({
          severity: 'warn',
          detail: 'Hanya baris komisi paling bawah yang dapat dihapus terlebih dahulu',
          group: 'bc',
          life: 3000
        })
      }
      return
    }

    tiers.value.splice(idx, 1)
    validateTiers('Tiered')
  }

  const onTierInput = (
    tier: ITierRow,
    field: 'minPassenger' | 'maxPassenger' | 'commissionAmount',
    e: any
  ) => {
    let val: any = e
    if (e && typeof e === 'object') {
      if ('value' in e) {
        val = e.value
      } else if (e.target && 'value' in e.target) {
        val = e.target.value
      }
    }

    const numVal = (val !== undefined && val !== null && val !== '' && !Number.isNaN(Number(val)))
      ? Number(val)
      : null

    tier[field] = numVal

    if (field === 'maxPassenger') {
      const idx = tiers.value.indexOf(tier)
      if (idx !== -1 && idx < tiers.value.length - 1) {
        const nextTier = tiers.value[idx + 1]
        nextTier.minPassenger = numVal !== null ? numVal + 1 : null
      }
    }

    nextTick(() => {
      validateTiers('Tiered')
    })
  }

  const setTiersFromData = (tiersList: any[], commissionType?: string | null) => {
    originalTiers.value = tiersList
      .filter((t: any) => !!(t.Id || t.id))
      .map((t: any) => ({
        id: (t.Id || t.id) as string,
        minPassenger: Number(t.MinPassengerCount ?? 0),
        maxPassenger: Number(t.MaxPassengerCount ?? 0),
        commissionAmount: Number(t.CommissionAmount ?? 0)
      }))

    if (commissionType === 'Tiered') {
      tiers.value = tiersList.map((t: any) => ({
        id: t.Id || t.id,
        minPassenger: t.MinPassengerCount,
        maxPassenger: t.MaxPassengerCount,
        commissionAmount: t.CommissionAmount,
        errorMin: '',
        errorMax: '',
        errorCommission: ''
      }))
    }
  }

  const resetTiers = () => {
    tiers.value = []
    originalTiers.value = []
    tiersGeneralError.value = ''
  }

  return {
    tiers,
    tiersGeneralError,
    originalTiers,
    validateTiers,
    addTierRow,
    removeTierRow,
    onTierInput,
    setTiersFromData,
    resetTiers
  }
}
